import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { Download, Smartphone } from '../icons/wp-icons';
import MobileInstallBanner from '../components/PWA/MobileInstallBanner';
import { clearBrandingCache } from '../components/BrandingLoader';
import { clearAllCache } from '../utils/offlineDB';
import { getHostBranding, applyHostBrandingMeta } from '../utils/hostBranding';

function Login() {
  const hostBranding = useMemo(() => getHostBranding(), []);
  const isHeykikiHost = !!hostBranding;
  const [logoFailed, setLogoFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const { login, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isIOS, isAndroid, isStandalone, showInstallOption, triggerInstall } = usePWAInstall();

  // Hole die urspruengliche URL aus dem state (von ProtectedRoute)
  const from = location.state?.from;

  // Idle-Logout-Hinweis: wenn ?reason=idle in der URL ist, zeige eine
  // freundliche Info, dass die Sitzung wegen Inaktivität beendet wurde
  // (Auto-Logout nach 60 Min, siehe AuthContext.IDLE_TIMEOUT_MS).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'idle') {
      setInfoMessage('Sie wurden wegen Inaktivität automatisch abgemeldet. Bitte erneut anmelden.');
    }
  }, [location.search]);

  // Pruefe ob Token in URL vorhanden (von Landing Page Registration)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl) {
      // Alle Caches löschen BEVOR neuer Token gesetzt wird
      clearBrandingCache();
      clearAllCache().then(async () => {
        // Token aus URL speichern
        localStorage.setItem('token', tokenFromUrl);
        // URL bereinigen (Token entfernen)
        window.history.replaceState({}, document.title, '/login');

        // User-Info abrufen um Onboarding-Status zu prüfen
        try {
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${tokenFromUrl}` }
          });
          const userData = await response.json();

          // Prüfen ob Onboarding abgeschlossen ist
          if (userData.organisation && !userData.organisation.onboardingCompleted) {
            window.location.href = '/onboarding';
          } else {
            window.location.href = '/dashboard';
          }
        } catch (error) {
          // Bei Fehler trotzdem zum Dashboard (AuthContext wird dann handeln)
          window.location.href = '/dashboard';
        }
      });
    }
  }, [location.search]);

  // Theme-Color + Document-Title für Login-Seite setzen (via shared util)
  useEffect(() => applyHostBrandingMeta(hostBranding, 'Login'), [hostBranding]);

  // Wenn User bereits eingeloggt ist, zur urspruenglichen URL weiterleiten
  useEffect(() => {
    if (user) {
      if (from) {
        navigate(from.pathname + (from.search || ''), { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, from, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, passwort);

    if (result.success) {
      // Nach erfolgreichem Login zur urspruenglichen URL navigieren
      if (from) {
        navigate(from.pathname + (from.search || ''), { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else if (result.needsVerification) {
      // E-Mail-Verifizierung erforderlich - neuer Code wurde bereits gesendet
      navigate('/verify-email', { state: { email: result.email }, replace: true });
    } else {
      setError(result.error);
      setShowForgotPassword(true);
    }

    setLoading(false);
  };

  const handleInstallClick = () => {
    if (isAndroid) {
      // Android: Direkt installieren
      triggerInstall();
    } else {
      // iOS: Banner mit Anleitung zeigen
      setShowInstallBanner(true);
    }
  };

  const accent = hostBranding?.accent || '#378ab2';
  const accentDark = hostBranding?.accentDark || '#2a6d8a';
  const logoSrc = hostBranding?.logo || '/icons/icon-512x512.png';
  const displayName = hostBranding?.name || 'WerkPilot';
  const subtitle = hostBranding?.subtitle || 'CRM System';
  const fontFamily = hostBranding?.fontFamily || "'Montserrat', sans-serif";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md">
        <div className="text-center mb-6">
          {isHeykikiHost && logoFailed ? (
            // Sauberer Platzhalter-Kreis mit Initialen, solange HeyKiki-Logo
            // noch nicht als /heykiki-logo.png im public-Ordner liegt. Besser
            // als ein fremdes WerkPilot-Icon auf einer HeyKiki-Domain.
            <div
              className="w-32 h-32 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: hostBranding.accent }}
            >
              <span className="text-white text-5xl font-bold" style={{ fontFamily }}>
                {displayName.substring(0, 2)}
              </span>
            </div>
          ) : (
            <img
              src={logoSrc}
              alt={`${displayName} Logo`}
              className="w-32 h-32 mx-auto mb-4 object-contain"
              onError={() => setLogoFailed(true)}
            />
          )}
          <span className="block text-2xl font-bold text-gray-900" style={{ fontFamily }}>{displayName}</span>
          <p className="text-text-muted">{subtitle}</p>
        </div>

        {infoMessage && !error && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
            {infoMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': accent }}
              placeholder="E-Mail-Adresse"
              required
              disabled={loading}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Passwort</label>
            <input
              type="password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': accent }}
              placeholder="Passwort"
              required
              disabled={loading}
            />
          </div>

          {showForgotPassword && (
            <div className="mb-6 text-right">
              <Link
                to="/forgot-password"
                className="text-sm hover:underline"
                style={{ color: accent }}
              >
                Passwort vergessen?
              </Link>
            </div>
          )}
          <button
            type="submit"
            className="w-full text-white py-3 rounded-button font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: accent }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = accentDark)}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = accent)}
            disabled={loading}
          >
            {loading ? 'Anmeldung laeuft...' : 'Anmelden'}
          </button>
        </form>

        {/* Install App Button - nur auf Mobile, nicht installiert, und nicht auf HeyKiki-Portal */}
        {showInstallOption && !isHeykikiHost && (
          <div className="mt-6 pt-6 border-t border-border">
            <button
              onClick={handleInstallClick}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 text-white rounded-xl font-medium transition active:scale-[0.98]"
              style={{ backgroundColor: accent }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = accentDark)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = accent)}
            >
              {isAndroid ? (
                <>
                  <Download className="w-5 h-5" />
                  App installieren
                </>
              ) : (
                <>
                  <Smartphone className="w-5 h-5" />
                  App installieren
                </>
              )}
            </button>
            <p className="text-xs text-center text-text-muted mt-2">
              {isIOS
                ? 'Für schnelleren Zugriff zum Home-Bildschirm hinzufügen'
                : 'Für Offline-Zugriff und schnelleren Start'}
            </p>
          </div>
        )}

        {/* Hinweis wenn bereits installiert - nicht auf HeyKiki-Portal */}
        {isStandalone && !isHeykikiHost && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-center gap-2 text-sm" style={{ color: accent }}>
              <Smartphone className="w-4 h-4" />
              <span>App ist installiert</span>
            </div>
          </div>
        )}

        {/* Register Link — bei Whitelabel-Hosts ausgeblendet, die dürfen
            nicht auf werkpilot.de verweisen. */}
        {!isHeykikiHost && (
          <div className="mt-6 pt-6 border-t border-border text-center">
            <p className="text-sm text-text-muted">
              Noch kein Konto?{' '}
              <a href="https://werkpilot.de/register" className="text-[#378ab2] hover:underline font-medium">
                Kostenlos registrieren
              </a>
            </p>
          </div>
        )}
      </div>

      {/* Install Banner Modal */}
      {showInstallBanner && (
        <MobileInstallBanner
          forceShow={true}
          onClose={() => setShowInstallBanner(false)}
        />
      )}
    </div>
  );
}

export default Login;
